import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { ArrowRight2Icon, Button, Tooltip } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { tradeSettingsAtom } from '@root/features/trade/trade-settings'
import { AntiMevShield } from '@root/features/trade/trade-settings/ui/desktop'
import { useTranslation } from '@root/shared/localization'
import { PetrolIcon, RunnerIcon, SharingHandIcon } from '@root/shared/ui-kit'

import { useGasSettingsValues } from '../../common/use-gas-settings-values'

import type { TradeSettings } from '@root/entities/trade-settings'

type Props = {
  onSettingsOpen: () => void
  tradeSettings?: TradeSettings.TradeSettings
  mevProtection?: 'full' | 'icon'
}

export const TradingGas: React.FC<Props> = ({
  onSettingsOpen,
  tradeSettings,
  mevProtection = 'full',
}) => {
  const { t } = useTranslation()

  const [activeTradeSettings] = useAtom(tradeSettingsAtom)
  const tradeSettingsResolved = tradeSettings ?? activeTradeSettings

  const { antiMevEnabled, bribe, priorityFee, slippage } = useGasSettingsValues(
    tradeSettingsResolved,
  )

  return (
    <Root>
      <Group>
        <Tooltip content={t('trading.order.gas-priority-fee-tooltip')}>
          <ButtonAlt
            type="button"
            iconLeft={<PetrolIcon />}
            radius="rounded"
            variant="soft"
            size="xxs"
            onClick={onSettingsOpen}
          >
            <Numeric value={priorityFee ?? 0} preset="currency-amount-short" />
          </ButtonAlt>
        </Tooltip>

        <Tooltip content={t('trading.order.slippage-percentage-tooltip')}>
          <ButtonAlt
            type="button"
            iconLeft={<RunnerIcon />}
            radius="rounded"
            variant="soft"
            size="xxs"
            onClick={onSettingsOpen}
          >
            <span>
              <Numeric value={slippage} preset="percents" />%
            </span>
          </ButtonAlt>
        </Tooltip>
        {bribe ? (
          <Tooltip content={t('trading.order.bribe-tooltip')}>
            <ButtonAlt
              type="button"
              iconLeft={<SharingHandIcon />}
              radius="rounded"
              variant="soft"
              size="xxs"
              onClick={onSettingsOpen}
            >
              <Numeric value={bribe} preset="amount-max-5-digits" />
            </ButtonAlt>
          </Tooltip>
        ) : undefined}
      </Group>
      <Right>
        <Mev type="button" onClick={onSettingsOpen}>
          <MevShieldIcon size={16} active={antiMevEnabled ?? false} />
          {mevProtection === 'full' ? (
            <span>{t('trading.mev_protection')}</span>
          ) : undefined}

          <ArrowRight2Icon size={16} color="var(--background-10)" />
        </Mev>
      </Right>
    </Root>
  )
}

const Root = styled.div`
  gap: 8px;

  display: grid;
  overflow: hidden;

  grid-template-columns: auto 1fr;

  align-items: center;

  padding-inline: 10px;
`

const Group = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  justify-content: start;
  gap: 4px;
`
const Right = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
`

const ButtonAlt = styled(Button)`
  --button-radius-rounded: 6px;

  background: var(--background-3);
  color: var(--text-11);

  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  padding: 0 8px;
`

const Mev = styled.button`
  all: unset;
  cursor: pointer;
  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;

  display: grid;
  align-items: center;
  justify-content: end;
  grid-template-columns: 1fr auto auto;
  justify-items: end;
  white-space: nowrap;
`

const MevShieldIcon = styled(AntiMevShield)`
  margin-inline-end: 3px;
`
